import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import GradientModal from './GradientModal';
import Button from './Button';

/**
 * BulkActionBar - Sticky toolbar shown during select mode for bulk operations.
 * 
 * @param {Object} props
 * @param {number} props.selectionCount - Number of currently selected items
 * @param {string} props.selectionLabel - Display label (e.g. "3 selected" or "3 of 8 items selected")
 * @param {boolean} props.isAllSelected - Whether all visible items are selected
 * @param {boolean} props.isIndeterminate - Whether some but not all visible items are selected
 * @param {Function} props.onToggleAll - Handler for Select All / Deselect All
 * @param {Function} props.onDelete - Handler for delete action (receives no args, parent handles IDs)
 * @param {Function} props.onCancel - Handler for exiting select mode
 * @param {boolean} props.deleting - Whether delete operation is in progress
 */
const BulkActionBar = ({
    selectionCount,
    selectionLabel,
    isAllSelected,
    isIndeterminate,
    onToggleAll,
    onDelete,
    onCancel,
    deleting = false,
}) => {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleDeleteClick = () => {
        if (selectionCount === 0) return;
        setShowConfirm(true);
    };

    const handleConfirmDelete = async () => {
        await onDelete();
        setShowConfirm(false);
    };

    return (
        <>
            <div className="sticky top-0 z-40 bg-white border-b-2 border-gray-200 shadow-sm px-4 sm:px-6 py-3 -mx-6 mb-6">
                <div className="flex items-center justify-between gap-3">
                    {/* Left: Select All + Count */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onToggleAll}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-[#2F5233]"
                            aria-label={isAllSelected ? 'Deselect All' : 'Select All'}
                        >
                            <SelectAllCheckbox
                                isAllSelected={isAllSelected}
                                isIndeterminate={isIndeterminate}
                            />
                            <span className="hidden sm:inline">
                                {isAllSelected ? 'Deselect All' : 'Select All'}
                            </span>
                        </button>
                        <span className="text-sm text-gray-600 font-medium">
                            {selectionLabel}
                        </span>
                    </div>

                    {/* Right: Delete + Cancel */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDeleteClick}
                            disabled={selectionCount === 0 || deleting}
                            aria-label="Delete selected"
                            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${selectionCount === 0 || deleting
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                        >
                            <Trash2 size={16} />
                            <span className="hidden sm:inline">Delete</span>
                        </button>
                        <button
                            onClick={onCancel}
                            aria-label="Cancel selection"
                            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                        >
                            <X size={16} />
                            <span className="hidden sm:inline">Cancel</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <GradientModal
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                title={`Delete ${selectionCount} item${selectionCount !== 1 ? 's' : ''}?`}
                maxWidth="max-w-sm"
            >
                <p className="text-gray-600 mb-6">This cannot be undone.</p>
                <div className="flex justify-center gap-3">
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => setShowConfirm(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                </div>
            </GradientModal>
        </>
    );
};

/**
 * SelectAllIndicator - Pure visual checkbox indicator (not an <input>).
 * Using a <span> instead of <input> avoids nesting interactive elements
 * inside the parent <button>, which is invalid HTML.
 */
const SelectAllCheckbox = ({ isAllSelected, isIndeterminate }) => (
    <span
        aria-hidden="true"
        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isAllSelected || isIndeterminate
            ? 'bg-[#52A675] border-[#52A675]'
            : 'border-gray-300 bg-white'
            }`}
    >
        {isAllSelected && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
        )}
        {isIndeterminate && !isAllSelected && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
            </svg>
        )}
    </span>
);

export default BulkActionBar;
